import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { History, X } from 'lucide-react';
import GoodsSharedContent from './GoodsSharedContent';
import HistoryPanelBodyLoading from './HistoryPanelBodyLoading';
import TechTaskInfoCell from './TechTaskInfoCell';
import { ImageCropInfoCell } from '@/image-crop';
import TimeRangePicker from '@/pages/components/TimeRangePicker';
import { taskAPI, imageFissionAPI, cropImageAPI, fundingAuthAPI } from '@/utils/api';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
  shouldShowTaskOperatorFilter,
  TASK_PERMISSION_SELF,
} from '@/pages/ipe/workbench/utils/taskHistoryPermissions';
import { showIpeDeleteConfirm } from '@/utils/ipeDeleteConfirm';
import { handleIpeError } from '@/pages/ipe/workbench/utils/errorHandler';
import {
  buildRetryTaskApiPatch,
  buildTerminatedTaskApiPatch,
  isRetryableTaskStatus,
  isTerminableTaskStatus,
} from '@/pages/ipe/workbench/utils/ipeTaskActions';
import { cancelIpeServerBatch } from '@/pages/ipe/workbench/utils/ipeBatchTaskCancel';
import { releaseIpeProcessingSlot } from '@/pages/ipe/workbench/utils/ipeProcessingQueue';
import { getImg2ImgTaskCompletionDisplay, isTaskInProgress } from '@/pages/ipe/workbench/utils/taskProgress';
import { useProcessingTasksPoll, useIpeTaskProgressSync } from '@/pages/ipe/workbench/hooks/useProcessingTasksPoll';
import { useVisibilityAwareInterval } from '@/pages/ipe/workbench/hooks/useVisibilityAwareInterval';
import {
  getAiStudioHistoryConfig,
  resolveImageCropTaskRecordId,
} from '@/pages/ipe/workbench/utils/aiStudioTaskHistory';
import { showToast as showGlobalToast } from '@/utils/toast';
import { enrichStudioTaskPersistedOutputs, taskNeedsStudioPreviewEnrich } from '@/pages/ipe/workbench/utils/enrichStudioTaskOutputs';
import { workbenchPreviewUrl, useWorkbenchPreviewPrefetch } from '@/utils/workbenchImagePreview';
import {
  loadFissionHistoryTasks,
  resolveFissionHistoryBadgeTaskId,
  resolveFissionServerBatchId,
} from '@/pages/ipe/workbench/utils/imageFissionTaskHistory';
import { buildHistoryTasksListQuery } from '@/pages/ipe/workbench/utils/historyTasksListQuery';
import { useHistoryPanelTransition } from '@/pages/ipe/workbench/hooks/useHistoryPanelTransition';
import { markTaskViewedFromHistory, removePendingTasks, reconcileHistoryBadgeWithServerTasks } from '@/utils/workbenchHistoryBadge';
import {
  buildHistoryPanelRestoreSnapshot,
  mergeDetailNavigateState,
} from '@/pages/ipe/workbench/utils/workbenchHistoryPanelRestore';
import { useCropHistoryVisiblePreview } from '@/pages/ipe/workbench/hooks/useCropHistoryVisiblePreview';
import { shouldIgnoreHistoryBackdropClose } from '@/pages/ipe/workbench/utils/nestedMediaPreviewGuard';

const getDefaultFilter = () => ({
  operatorUserId: '',
  subAccount: '',
  status: '',
  startDate: '',
  endDate: '',
});

const ENRICH_HISTORY_PREVIEW_NAV_IDS = new Set([
  // imageCrop 走 useCropHistoryVisiblePreview（可视行 + listBatchPage），不在此全页 enrich
  'imageEnhance',
  'smartCutout',
  'stampExtract',
  'aiOutpaint',
]);

const HISTORY_PREVIEW_ENRICH_CONCURRENCY = 4;

async function enrichHistoryPreviewTasks(tasks, navId, formatTask) {
  const targets = (tasks || []).filter((task) => task?.id && taskNeedsStudioPreviewEnrich(task));
  if (!targets.length) return new Map();

  const patches = new Map();
  let cursor = 0;
  const worker = async () => {
    while (cursor < targets.length) {
      const index = cursor;
      cursor += 1;
      const task = targets[index];
      try {
        const enriched = await enrichStudioTaskPersistedOutputs(task, navId, { previewOnly: true });
        if (enriched !== task) {
          patches.set(String(task.id), formatTask(enriched));
        }
      } catch {
        /* ignore single row */
      }
    }
  };
  await Promise.all(
    Array.from(
      { length: Math.min(HISTORY_PREVIEW_ENRICH_CONCURRENCY, targets.length) },
      () => worker(),
    ),
  );
  return patches;
}

export default function AiStudioTaskHistoryPanel({
  navId,
  open,
  onClose,
  showToast,
  operatorName = '',
  onViewDetail,
  keepOpenOnViewDetail = false,
  detailOpen = false,
  onCloseDetail,
  refreshKey: externalRefreshKey = 0,
  taskExtraColumnHeader = '',
  renderTaskExtraCell = null,
  initialRestore = null,
  onRestoreApplied,
}) {
  const navigate = useNavigate();
  const { user } = useIpeAuth();
  const { t } = useTranslation('workbench');
  const workspaceMode = useIpeWorkspaceMode();
  const currentUserId = user?.id != null ? String(user.id) : (user?._id != null ? String(user._id) : '');
  const [taskPermissionScope, setTaskPermissionScope] = useState(TASK_PERMISSION_SELF);
  const [operatorFilterOptions, setOperatorFilterOptions] = useState(() => [
    { value: '', label: i18n.t('workbench:taskHistory.allOperators') },
  ]);
  const showOperatorFilter = shouldShowTaskOperatorFilter(workspaceMode, taskPermissionScope);
  const config = useMemo(() => getAiStudioHistoryConfig(navId), [navId]);
  const {
    mounted,
    exiting,
    contentReady,
    historyClassName,
    requestClose,
    handlePanelAnimationEnd,
  } = useHistoryPanelTransition(open, onClose);
  const [filterInputs, setFilterInputs] = useState(getDefaultFilter);
  const [filters, setFilters] = useState(getDefaultFilter);
  const [tasks, setTasks] = useState([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isQueryLoading] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const listLoadGenRef = useRef(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [retryingTaskId, setRetryingTaskId] = useState('');
  const [terminatingTaskId, setTerminatingTaskId] = useState('');
  /** 同步锁：防连点在 setState 生效前重复提交 */
  const actionLockRef = useRef('');
  const panelBodyRef = useRef(null);
  const [scrollHighlightTaskId, setScrollHighlightTaskId] = useState('');
  const restoreSessionRef = useRef('');
  const workspaceEffectReadyRef = useRef(false);

  const toast = useCallback((message, type = 'success') => {
    if (typeof showToast === 'function') {
      showToast(message, type);
      return;
    }
    showGlobalToast(message, type);
  }, [showToast]);

  const formatTask = useCallback((task) => config.formatTask(task), [config]);

  const renderInfoCell = useCallback((task) => {
    if (navId === 'imageCrop') {
      return <ImageCropInfoCell task={task} />;
    }
    return (
      <TechTaskInfoCell
        task={task}
        getCompletionDisplay={config.getCompletionDisplay || getImg2ImgTaskCompletionDisplay}
        operatorName={operatorName}
        countUnit={config.taskType === 'aiCopywriting' ? t('studioChrome.unitItem') : t('studioChrome.unitSheet')}
        topExtra={config.taskType === 'aiCopywriting' && task._languageLabel ? (
          <span className="crop-task-info-lang">{t('studioChrome.outputLangLine', { lang: task._languageLabel })}</span>
        ) : null}
      />
    );
  }, [navId, config.getCompletionDisplay, config.taskType, operatorName, t]);

  const isFissionSource = config.dataSource === 'fission';
  const badgeScopeId = config.badgeScopeId || navId;

  useEffect(() => {
    const shouldRefreshList = workspaceEffectReadyRef.current;
    workspaceEffectReadyRef.current = true;
    if (workspaceMode !== 'team') {
      setTaskPermissionScope(TASK_PERMISSION_SELF);
      setOperatorFilterOptions([{ value: '', label: i18n.t('workbench:taskHistory.allOperators') }]);
      setFilterInputs((prev) => (prev.operatorUserId ? { ...prev, operatorUserId: '' } : prev));
      setFilters((prev) => (prev.operatorUserId ? { ...prev, operatorUserId: '' } : prev));
      if (shouldRefreshList) {
        setPage(1);
        setRefreshKey((v) => v + 1);
      }
      return undefined;
    }
    let cancelled = false;
    fundingAuthAPI.getTeamOverview()
      .then((data) => {
        if (cancelled) return;
        const scope = data?.currentMember?.permissionScope || TASK_PERMISSION_SELF;
        setTaskPermissionScope(scope);
        const members = Array.isArray(data?.members) ? data.members : [];
        setOperatorFilterOptions([
          { value: '', label: i18n.t('workbench:taskHistory.allOperators') },
          ...members.map((member) => {
            const memberId = member.userId != null ? String(member.userId) : '';
            const baseLabel = member.nickname
              || member.phoneMasked
              || i18n.t('workbench:taskHistory.memberFallback', { id: member.userId });
            const isMe = memberId && memberId === currentUserId;
            return {
              value: memberId,
              label: isMe ? i18n.t('workbench:taskHistory.meSuffix', { name: baseLabel }) : baseLabel,
            };
          }),
        ]);
        if (shouldRefreshList) {
          setPage(1);
          setRefreshKey((v) => v + 1);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTaskPermissionScope(TASK_PERMISSION_SELF);
          setOperatorFilterOptions([{ value: '', label: i18n.t('workbench:taskHistory.allOperators') }]);
          if (shouldRefreshList) {
            setPage(1);
            setRefreshKey((v) => v + 1);
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceMode, currentUserId]);

  useEffect(() => {
    if (showOperatorFilter) return;
    setFilterInputs((prev) => (prev.operatorUserId ? { ...prev, operatorUserId: '' } : prev));
    setFilters((prev) => (prev.operatorUserId ? { ...prev, operatorUserId: '' } : prev));
  }, [showOperatorFilter]);

  /** 拉取当前筛选/分页列表；不写 state，由调用方在 gen/cancelled 守卫后再 apply */
  const loadTaskList = useCallback(async () => {
    if (isFissionSource) {
      const { tasks: rows, total } = await loadFissionHistoryTasks({
        page,
        pageSize,
        ownerUserId: filters.operatorUserId || undefined,
      });
      return { tasks: rows, total: Number(total) || 0 };
    }
    const query = buildHistoryTasksListQuery(filters, {
      type: config.taskType,
      page,
      pageSize,
    });
    const res = await taskAPI.getAll(query);
    return {
      tasks: (res?.tasks || []).map(formatTask),
      total: Number(res?.total) || 0,
    };
  }, [config.taskType, filters, formatTask, isFissionSource, page, pageSize]);

  /** 对齐 Goods：仅当 gen 仍为当前代且未卸载时才写入列表，避免翻页/筛选竞态错页 */
  const applyLoadedListIfCurrent = useCallback((gen, rows, total) => {
    if (listLoadGenRef.current !== gen) return false;
    setTasks(Array.isArray(rows) ? rows : []);
    setTotalTasks(Number(total) || 0);
    return true;
  }, []);

  useEffect(() => {
    if (!contentReady || exiting) return undefined;
    if (!isFissionSource && !config.taskType) return undefined;
    const gen = listLoadGenRef.current + 1;
    listLoadGenRef.current = gen;
    let cancelled = false;
    setIsListLoading(true);
    (async () => {
      try {
        const { tasks: rows, total } = await loadTaskList();
        if (cancelled || listLoadGenRef.current !== gen) return;
        applyLoadedListIfCurrent(gen, rows, total);
      } catch (e) {
        if (!cancelled && listLoadGenRef.current === gen) {
          console.error(`加载 ${config.toolLabel} 任务失败:`, e);
          applyLoadedListIfCurrent(gen, [], 0);
        }
      } finally {
        if (!cancelled && listLoadGenRef.current === gen) {
          setIsListLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    contentReady,
    exiting,
    refreshKey,
    externalRefreshKey,
    config.taskType,
    config.toolLabel,
    loadTaskList,
    applyLoadedListIfCurrent,
    isFissionSource,
  ]);

  // P1：历史面板单路进度 —— 仅 fetchProgressBatch（覆盖进行中 + serverWorker 未终态）
  // 详情页仍走 SSE；此处不再叠加 SSE / 多路 poll / 全量 getAll
  useProcessingTasksPoll(
    !isFissionSource && contentReady && !exiting ? tasks : [],
    setTasks,
    {
      formatTask,
      skipWhenBackgroundActive: navId === 'imageCrop',
      includeServerWorkerPending: true,
      intervalMs: 4000,
      backgroundIntervalMs: 12_000,
      enabled: Boolean(config.taskType),
    },
  );

  // 裁剪本地后台处理器推送（非 HTTP 轮询）
  useIpeTaskProgressSync(
    navId === 'imageCrop' ? 'imageCrop' : '',
    setTasks,
    formatTask,
    { enabled: navId === 'imageCrop' && contentReady && !exiting },
  );

  const hasProcessingFission = useMemo(
    () => isFissionSource && tasks.some((task) => task?.id && isTaskInProgress(task.status)),
    [isFissionSource, tasks],
  );

  // 裂变无 /tasks/progress：单路全量刷新（不叠加其它 poll）；仍受 gen 守卫，避免盖住翻页结果
  useVisibilityAwareInterval(
    async () => {
      const gen = listLoadGenRef.current;
      try {
        const { tasks: rows, total } = await loadTaskList();
        applyLoadedListIfCurrent(gen, rows, total);
      } catch {
        /* ignore poll errors */
      }
    },
    {
      enabled: Boolean(contentReady && !exiting && isFissionSource && hasProcessingFission),
      intervalMs: 4000,
      backgroundIntervalMs: 15_000,
      runImmediately: false,
    },
  );

  useEffect(() => {
    if (!mounted || exiting) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      // 命令式删除确认在另一 React root，避免 Esc 同时关掉历史侧栏
      if (document.querySelector('.ipe-delete-confirm')) return;
      // 图片/视频预览叠在历史之上时，Esc 只关预览
      if (shouldIgnoreHistoryBackdropClose()) return;
      requestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mounted, exiting, requestClose]);

  const paginatedTasks = tasks;

  useEffect(() => {
    if (!open || !initialRestore || initialRestore.navId !== navId) return undefined;
    const sessionKey = JSON.stringify(initialRestore);
    if (restoreSessionRef.current === sessionKey) return undefined;
    restoreSessionRef.current = sessionKey;

    const {
      page: restorePage,
      pageSize: restorePageSize,
      filters: restoreFilters,
      filterInputs: restoreFilterInputs,
      highlightTaskId,
    } = initialRestore;

    if (restoreFilters && typeof restoreFilters === 'object') {
      setFilters({ ...getDefaultFilter(), ...restoreFilters });
    }
    if (restoreFilterInputs && typeof restoreFilterInputs === 'object') {
      setFilterInputs({ ...getDefaultFilter(), ...restoreFilterInputs });
    }
    if (Number.isFinite(restorePage) && restorePage > 0) {
      setPage(restorePage);
    }
    if (Number.isFinite(restorePageSize) && restorePageSize > 0) {
      setPageSize(restorePageSize);
    }
    if (highlightTaskId) {
      setScrollHighlightTaskId(String(highlightTaskId));
    }
    if (typeof onRestoreApplied === 'function') {
      onRestoreApplied();
    }
    return undefined;
  }, [open, initialRestore, navId, onRestoreApplied]);

  useEffect(() => {
    if (!open || !scrollHighlightTaskId || isQueryLoading || isListLoading) return undefined;
    const timer = window.setTimeout(() => {
      const root = panelBodyRef.current;
      if (!root) return;
      const selector = `[data-history-task-id="${CSS.escape(scrollHighlightTaskId)}"]`;
      const row = root.querySelector(selector);
      if (!row) return;
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      row.classList.add('wf-history-row--highlight');
      window.setTimeout(() => {
        row.classList.remove('wf-history-row--highlight');
      }, 2400);
      setScrollHighlightTaskId('');
    }, 120);
    return () => window.clearTimeout(timer);
  }, [open, scrollHighlightTaskId, isQueryLoading, isListLoading, paginatedTasks]);

  const historyPreviewAttemptedRef = useRef(new Set());
  const cropPreviewAttemptedRef = useRef(new Set());

  useEffect(() => {
    if (!open) {
      historyPreviewAttemptedRef.current.clear();
      cropPreviewAttemptedRef.current.clear();
    }
  }, [open]);

  const historyPreviewTargetKey = useMemo(
    () => paginatedTasks.map((task) => String(task?.id || '')).filter(Boolean).join('|'),
    [paginatedTasks],
  );

  useEffect(() => {
    if (!contentReady || exiting || !ENRICH_HISTORY_PREVIEW_NAV_IDS.has(navId)) return undefined;
    let cancelled = false;
    void (async () => {
      const targets = paginatedTasks.filter((task) => {
        const id = String(task?.id || '');
        if (!id || historyPreviewAttemptedRef.current.has(id)) return false;
        return taskNeedsStudioPreviewEnrich(task);
      });
      if (!targets.length) return;
      targets.forEach((task) => historyPreviewAttemptedRef.current.add(String(task.id)));
      const patchMap = await enrichHistoryPreviewTasks(targets, navId, formatTask);
      if (cancelled || !patchMap.size) return;
      setTasks((prev) => {
        let changed = false;
        const next = prev.map((row) => {
          const patch = patchMap.get(String(row.id));
          if (!patch) return row;
          if (
            patch.previewUrl === row.previewUrl
            && patch.previewViewUrl === row.previewViewUrl
            && patch.previewDisplayUrl === row.previewDisplayUrl
            && patch._outputPreview === row._outputPreview
          ) {
            return row;
          }
          changed = true;
          return patch;
        });
        return changed ? next : prev;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [contentReady, exiting, navId, historyPreviewTargetKey, paginatedTasks, formatTask]);

  // 裁剪历史预览：可视行 + 有界并发，避免打开时整页 Promise.all(listBatchPage)
  useCropHistoryVisiblePreview({
    enabled: Boolean(contentReady && !exiting && navId === 'imageCrop'),
    tasks: paginatedTasks,
    rootRef: panelBodyRef,
    formatTask,
    setTasks,
    attemptedRef: cropPreviewAttemptedRef,
  });

  const previewUrls = useMemo(
    () => {
      const urls = [];
      paginatedTasks.forEach((task) => {
        const raw = String(config.getTaskPreviewUrl(task) || '').trim();
        if (raw) urls.push(raw);
        const display = String(task.previewDisplayUrl || task.previewViewUrl || '').trim();
        if (display && display !== raw) urls.push(display);
      });
      return urls;
    },
    [paginatedTasks, config],
  );

  useWorkbenchPreviewPrefetch(previewUrls, {
    enabled: contentReady && !exiting,
  });

  const resolveTaskPreviewUrl = useCallback((task) => {
    const raw = String(config.getTaskPreviewUrl(task) || '').trim();
    if (!raw) return '';
    if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
    const sync = workbenchPreviewUrl(raw, {
      displayUrl: task?.previewDisplayUrl,
      viewUrl: task?.previewViewUrl,
    });
    // 签名缓存未就绪时仍返回 canonical，交给 SignedImage 异步签名，避免整格显示「暂无」
    return sync || raw;
  }, [config]);

  const handleResetFilters = useCallback(() => {
    const defaultFilter = getDefaultFilter();
    setFilterInputs(defaultFilter);
    setFilters(defaultFilter);
    setPage(1);
    setRefreshKey((v) => v + 1);
  }, []);

  const handleQuery = () => {
    setFilters({ ...filterInputs });
    setPage(1);
    setRefreshKey((v) => v + 1);
  };

  const resolveHistoryBadgeTaskId = useCallback((task) => {
    if (typeof config.resolveHistoryBadgeTaskId === 'function') {
      return config.resolveHistoryBadgeTaskId(task);
    }
    if (isFissionSource) {
      return resolveFissionHistoryBadgeTaskId(task);
    }
    return task?.id;
  }, [config, isFissionSource]);

  useEffect(() => {
    if (!contentReady || exiting || isListLoading) return undefined;
    reconcileHistoryBadgeWithServerTasks(
      badgeScopeId,
      paginatedTasks,
      workspaceMode,
      {
        total: totalTasks,
        resolveTaskId: resolveHistoryBadgeTaskId,
      },
    );
    return undefined;
  }, [
    badgeScopeId,
    contentReady,
    exiting,
    isListLoading,
    paginatedTasks,
    resolveHistoryBadgeTaskId,
    totalTasks,
    workspaceMode,
  ]);

  const handleViewDetail = (task) => {
    const badgeTaskId = resolveHistoryBadgeTaskId(task);
    if (badgeTaskId) {
      markTaskViewedFromHistory(badgeScopeId, badgeTaskId, workspaceMode);
    }
    const fromHistory = buildHistoryPanelRestoreSnapshot(navId, {
      page,
      pageSize,
      filters,
      filterInputs,
      highlightTaskId: badgeTaskId || task?.id,
    });
    if (typeof onViewDetail === 'function') {
      onViewDetail(task, fromHistory);
      if (!keepOpenOnViewDetail) {
        requestClose();
      }
      return;
    }
    const batchKey = isFissionSource
      ? resolveFissionServerBatchId(task)
      : (task.id || config.resolveBatchNo(task));
    if (config.detailPath && batchKey) {
      const baseState = typeof config.getDetailNavigateState === 'function'
        ? config.getDetailNavigateState(task)
        : { task };
      navigate(config.detailPath(batchKey), {
        state: mergeDetailNavigateState(baseState, fromHistory),
      });
      requestClose();
      return;
    }
    toast(i18n.t('workbench:taskHistory.detailComingSoon'), 'info');
  };

  const handleTerminateTask = useCallback(async (task) => {
    if (!task?.id || !isTerminableTaskStatus(task.status)) {
      toast(i18n.t('workbench:taskHistory.cannotTerminate'), 'info');
      return;
    }
    if (actionLockRef.current || terminatingTaskId || retryingTaskId || isDeleting) return;
    actionLockRef.current = `terminate:${task.id}`;
    setTerminatingTaskId(task.id);
    try {
      if (isFissionSource) {
        const batchId = resolveFissionServerBatchId(task);
        if (!batchId) {
          toast(i18n.t('workbench:taskHistory.terminateMissingBatch'), 'error');
          return;
        }
        try {
          await imageFissionAPI.cancelBatch(batchId);
          const gen = listLoadGenRef.current;
          const { tasks: rows, total } = await loadTaskList();
          applyLoadedListIfCurrent(gen, rows, total);
          toast(i18n.t('workbench:taskHistory.taskTerminated'), 'success');
        } catch (e) {
          toast(e.message || i18n.t('workbench:taskHistory.terminateFailed'), 'error');
        }
        return;
      }
      if (typeof config.cancelBeforeServer === 'function') {
        const cancelId = navId === 'imageCrop'
          ? (task.taskId || task.id)
          : task.id;
        try {
          await Promise.resolve(config.cancelBeforeServer(cancelId));
        } catch (e) {
          toast(
            handleIpeError(
              i18n.t('workbench:taskHistory.terminateTaskAction'),
              e,
              i18n.t('workbench:taskHistory.terminateTaskFailed'),
            ),
            'error',
          );
          return;
        }
      }
      // 视频 cancel-video 等：服务端已写终态，勿再 update 成 partial 覆盖 phase/reason
      if (config.cancelCompletesOnServer) {
        await releaseIpeProcessingSlot(task.taskId || task.id);
        const gen = listLoadGenRef.current;
        try {
          const { tasks: rows, total } = await loadTaskList();
          applyLoadedListIfCurrent(gen, rows, total);
        } catch {
          /* 列表刷新失败时仍提示已终止；下次打开会同步 */
        }
        toast(i18n.t('workbench:taskHistory.taskTerminated'), 'success');
        return;
      }
      if (!config.skipServerBatchCancel) {
        await cancelIpeServerBatch(task.taskId || task.id);
      }
      const total = Number(task.total) || 0;
      const patch = buildTerminatedTaskApiPatch({
        total,
        successCount: Number(task.successCount) || 0,
        failedCount: Number(task.failedCount) || 0,
      });
      let resultPayload = task.result && typeof task.result === 'object' ? { ...task.result } : {};
      resultPayload = {
        ...resultPayload,
        batchPhase: 'cancelled',
        serverWorker: true,
      };
      setTasks((prev) =>
        prev.map((row) => (
          String(row.id) === String(task.id)
            ? { ...row, ...patch, result: resultPayload }
            : row
        )),
      );
      try {
        const res = await taskAPI.getById(task.id);
        const full = formatTask(res?.task || res);
        if (full?.result) {
          resultPayload = {
            ...full.result,
            batchPhase: 'cancelled',
            serverWorker: true,
          };
        }
      } catch {
        /* use list row */
      }
      try {
        await taskAPI.update(task.id, { ...patch, result: resultPayload });
        await releaseIpeProcessingSlot(task.taskId || task.id);
        setTasks((prev) =>
          prev.map((row) => (String(row.id) === String(task.id) ? { ...row, ...patch, result: resultPayload } : row)),
        );
        toast(i18n.t('workbench:taskHistory.taskTerminated'), 'success');
      } catch (e) {
        toast(
          handleIpeError(
            i18n.t('workbench:taskHistory.terminateTaskAction'),
            e,
            i18n.t('workbench:taskHistory.terminateTaskFailed'),
          ),
          'error',
        );
      }
    } finally {
      actionLockRef.current = '';
      setTerminatingTaskId('');
    }
  }, [applyLoadedListIfCurrent, config, formatTask, isDeleting, isFissionSource, loadTaskList, navId, retryingTaskId, terminatingTaskId, toast]);

  const handleRetryTask = useCallback(async (task) => {
    if (!task?.id || !isRetryableTaskStatus(task.status)) {
      toast(i18n.t('workbench:taskHistory.cannotRetry'), 'info');
      return;
    }
    if (actionLockRef.current || terminatingTaskId || retryingTaskId || isDeleting) return;
    actionLockRef.current = `retry:${task.id}`;
    setRetryingTaskId(String(task.id));
    try {
      if (isFissionSource) {
        const batchId = resolveFissionServerBatchId(task);
        if (!batchId) {
          toast(i18n.t('workbench:taskHistory.retryMissingBatch'), 'error');
          return;
        }
        try {
          await imageFissionAPI.resumeBatch(batchId);
          const gen = listLoadGenRef.current;
          const { tasks: rows, total } = await loadTaskList();
          applyLoadedListIfCurrent(gen, rows, total);
          toast(i18n.t('workbench:taskHistory.taskResumed'), 'success');
        } catch (e) {
          toast(e.message || i18n.t('workbench:taskHistory.retryFailed'), 'error');
        }
        return;
      }
      let full = task;
      try {
        const res = await taskAPI.getById(task.id);
        full = formatTask(res?.task || res);
      } catch {
        /* use list row */
      }
      const totalUnits = Number(full.total) || 1;
      const apiPatch = buildRetryTaskApiPatch({ total: totalUnits, successCount: 0, failedCount: 0 });
      setTasks((prev) => prev.map((row) => (
        String(row.id) === String(full.id)
          ? formatTask({
            ...row,
            ...apiPatch,
            result: {
              ...(row.result && typeof row.result === 'object' ? row.result : {}),
              content: '',
              text: '',
              failureHint: '',
            },
          })
          : row
      )));
      toast(i18n.t('workbench:taskHistory.retrying'), 'info');
      try {
        if (typeof config.retryTask === 'function') {
          await config.retryTask(full);
          try {
            const res = await taskAPI.getById(full.id);
            const formatted = formatTask(res?.task || res);
            setTasks((prev) => prev.map((row) => (
              String(row.id) === String(full.id) ? formatted : row
            )));
          } catch {
            setTasks((prev) => prev.map((row) => (
              String(row.id) === String(full.id) ? { ...row, ...apiPatch, status: 'success' } : row
            )));
          }
          setRefreshKey((k) => k + 1);
          toast(config.retryToast, 'success');
          return;
        }

        await taskAPI.update(full.id, apiPatch);
        await taskAPI.submit(full.id, { force: true });
        setTasks((prev) =>
          prev.map((row) => (String(row.id) === String(full.id) ? { ...row, ...apiPatch } : row)),
        );
        setRefreshKey((k) => k + 1);
        toast(config.retryToast, 'success');
      } catch (e) {
        try {
          const res = await taskAPI.getById(full.id);
          const formatted = formatTask(res?.task || res);
          setTasks((prev) => prev.map((row) => (
            String(row.id) === String(full.id) ? formatted : row
          )));
        } catch {
          /* keep optimistic processing row */
        }
        toast(
          handleIpeError(
            i18n.t('workbench:taskHistory.retryTaskAction'),
            e,
            i18n.t('workbench:taskHistory.retryFailedRetry'),
          ),
          'error',
        );
      }
    } finally {
      actionLockRef.current = '';
      setRetryingTaskId('');
    }
  }, [applyLoadedListIfCurrent, config, formatTask, isDeleting, isFissionSource, loadTaskList, retryingTaskId, terminatingTaskId, toast]);

  const resolveDeleteTaskId = useCallback((task) => {
    if (!task) return '';
    if (navId === 'imageCrop') {
      return resolveImageCropTaskRecordId(task);
    }
    return String(task.id || task.taskId || '').trim();
  }, [navId]);

  const handleRequestDeleteTasks = useCallback(async (taskIds) => {
    const ids = (taskIds || []).map(String).filter(Boolean);
    if (ids.length === 0) return;
    if (actionLockRef.current || terminatingTaskId || retryingTaskId || isDeleting) return;

    actionLockRef.current = `delete:${ids.join(',')}`;
    setIsDeleting(true);
    try {
      const confirmed = await showIpeDeleteConfirm({
        message: (() => {
          const toolLabel = i18n.t(`workbench:nav.${navId}.label`, {
            defaultValue: config.toolLabel,
          });
          return ids.length > 1
            ? i18n.t('workbench:taskHistory.deleteConfirmMultiple', {
              count: ids.length,
              label: toolLabel,
            })
            : i18n.t('workbench:taskHistory.deleteConfirmSingle', { label: toolLabel });
        })(),
      });
      if (!confirmed) return;

      if (isFissionSource) {
        const results = await Promise.allSettled(
          ids.map((id) => {
            const task = tasks.find((t) => String(t.id) === id || String(t.subAccount) === id);
            const batchId = task ? resolveFissionServerBatchId(task) : id;
            return imageFissionAPI.deleteBatch(batchId);
          }),
        );
        const succeededIds = ids.filter((_, index) => results[index].status === 'fulfilled');
        const failedCount = ids.length - succeededIds.length;

        if (succeededIds.length > 0) {
          removePendingTasks(
            badgeScopeId,
            succeededIds.map((sid) => {
              const task = tasks.find((t) => String(t.id) === sid || String(t.subAccount) === sid);
              return task ? resolveHistoryBadgeTaskId(task) : sid;
            }).filter(Boolean),
            workspaceMode,
          );
          const succeededSet = new Set(succeededIds.map(String));
          setTasks((prev) => prev.filter((t) => !succeededSet.has(String(t.id)) && !succeededSet.has(String(t.subAccount))));
          const gen = listLoadGenRef.current;
          const { tasks: rows, total } = await loadTaskList();
          applyLoadedListIfCurrent(gen, rows, total);
        }

        if (failedCount === 0) {
          toast(i18n.t('workbench:taskHistory.deletedCount', { count: succeededIds.length }), 'success');
        } else if (succeededIds.length > 0) {
          const firstReject = results.find((r) => r.status === 'rejected');
          toast(
            i18n.t('workbench:taskHistory.deletedPartial', {
              count: succeededIds.length,
              failed: failedCount,
              reason: firstReject?.reason?.message || i18n.t('workbench:taskHistory.pleaseRetry'),
            }),
            'warning',
          );
        } else {
          const firstReject = results.find((r) => r.status === 'rejected');
          toast(
            firstReject?.reason?.message || i18n.t('workbench:taskHistory.deleteFailed'),
            'error',
          );
        }
        return;
      }

      await Promise.allSettled(
        ids.map(async (id) => {
          const task = tasks.find((t) => {
            const tid = resolveDeleteTaskId(t);
            return tid === id || String(t.id) === id || String(t.subAccount) === id;
          });
          if (task && isTaskInProgress(task.status)) {
            const cancelId = resolveDeleteTaskId(task) || task.id;
            if (typeof config.cancelBeforeServer === 'function') {
              await Promise.resolve(config.cancelBeforeServer(cancelId));
            }
            if (!config.skipServerBatchCancel) {
              await cancelIpeServerBatch(cancelId);
            }
          }
        }),
      );

      // 先从列表摘除，避免删除接口阻塞时界面看起来像卡死
      const pendingSet = new Set(ids.map(String));
      setTasks((prev) => prev.filter((t) => {
        const tid = resolveDeleteTaskId(t);
        return !pendingSet.has(tid) && !pendingSet.has(String(t.id));
      }));

      const results = await Promise.allSettled(
        ids.map(async (id) => {
          if (navId === 'imageCrop') {
            // 仅清理裁剪任务元数据记录，不删图片空间文件
            await cropImageAPI.deleteByTask(id);
          }
          await taskAPI.delete(id);
        }),
      );
      const succeededIds = ids.filter((_, index) => results[index].status === 'fulfilled');
      const failedCount = ids.length - succeededIds.length;
      const failedIds = ids.filter((_, index) => results[index].status === 'rejected');

      if (succeededIds.length > 0) {
        removePendingTasks(badgeScopeId, succeededIds, workspaceMode);
      }
      // 删除失败的行重新拉列表恢复，避免乐观删除后“幽灵消失”
      if (failedIds.length > 0) {
        try {
          const gen = listLoadGenRef.current;
          const { tasks: rows, total } = await loadTaskList();
          applyLoadedListIfCurrent(gen, rows, total);
        } catch {
          /* keep optimistic list */
        }
      }

      if (failedCount === 0) {
        toast(i18n.t('workbench:taskHistory.deletedCount', { count: succeededIds.length }), 'success');
      } else if (succeededIds.length > 0) {
        const firstReject = results.find((r) => r.status === 'rejected');
        toast(
          i18n.t('workbench:taskHistory.deletedPartial', {
            count: succeededIds.length,
            failed: failedCount,
            reason: firstReject?.reason?.message || i18n.t('workbench:taskHistory.pleaseRetry'),
          }),
          'warning',
        );
      } else {
        const firstReject = results.find((r) => r.status === 'rejected');
        toast(
          handleIpeError(
            i18n.t('workbench:taskHistory.deleteTaskAction'),
            firstReject?.reason,
            i18n.t('workbench:taskHistory.deleteFailed'),
          ),
          'error',
        );
      }
    } catch (e) {
      toast(e.message || i18n.t('workbench:taskHistory.deleteFailed'), 'error');
    } finally {
      actionLockRef.current = '';
      setIsDeleting(false);
    }
  }, [
    applyLoadedListIfCurrent,
    badgeScopeId,
    config,
    isDeleting,
    isFissionSource,
    loadTaskList,
    navId,
    resolveDeleteTaskId,
    resolveHistoryBadgeTaskId,
    retryingTaskId,
    tasks,
    terminatingTaskId,
    toast,
    workspaceMode,
  ]);

  const handleDeleteTask = useCallback((task) => {
    const id = resolveDeleteTaskId(task);
    if (!id) {
      toast(i18n.t('workbench:taskHistory.taskNotRecognized'), 'error');
      return;
    }
    handleRequestDeleteTasks([id]);
  }, [handleRequestDeleteTasks, resolveDeleteTaskId, toast]);

  const handleBackdropClick = useCallback((event) => {
    // 叠层预览打开/刚关闭：忽略（含 click 穿透）
    if (shouldIgnoreHistoryBackdropClose()) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      return;
    }
    if (detailOpen && typeof onCloseDetail === 'function') {
      onCloseDetail();
      return;
    }
    requestClose();
  }, [detailOpen, onCloseDetail, requestClose]);

  if (!mounted || typeof document === 'undefined') return null;

  const panelId = `${navId || 'ai-studio'}-history`;
  const portalClassName = [
    historyClassName,
    detailOpen ? 'is-detail-open' : '',
  ].filter(Boolean).join(' ');

  return createPortal(
    <div className={portalClassName} data-ipe-control-skin="soft">
      <button
        type="button"
        className="img2img-history__backdrop"
        aria-label={detailOpen ? t('studioChrome.close') : t('studioChrome.closeHistory')}
        onMouseDown={(event) => {
          if (shouldIgnoreHistoryBackdropClose()) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        onClick={handleBackdropClick}
      />
      <aside
        className="img2img-history__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${panelId}-title`}
        onAnimationEnd={handlePanelAnimationEnd}
      >
        <header className="img2img-history__header">
          <div className="img2img-history__title-wrap">
            <History size={18} aria-hidden="true" />
            <h2 id={`${panelId}-title`}>{t('studioChrome.history')}</h2>
          </div>
          <button type="button" className="img2img-history__close" onClick={requestClose} aria-label={t('studioChrome.close')}>
            <X size={18} />
          </button>
        </header>
        <div className="img2img-history__body" ref={panelBodyRef}>
          {contentReady ? (
          <GoodsSharedContent
            filterInputs={filterInputs}
            onInputChange={(key, value) => setFilterInputs((prev) => ({ ...prev, [key]: value }))}
            onQuery={handleQuery}
            onResetFilters={handleResetFilters}
            tasks={paginatedTasks}
            onViewDetail={handleViewDetail}
            onTerminateTask={handleTerminateTask}
            onRetryTask={handleRetryTask}
            retryingTaskId={retryingTaskId}
            terminatingTaskId={terminatingTaskId}
            onDeleteTask={handleDeleteTask}
            plainTextTaskActions
            isCropMode
            batchCellLayout="meta"
            renderInfoCell={renderInfoCell}
            tableClassName="funding-table--img2img-lite"
            batchCellIdOnly
            showOperatorFilter={showOperatorFilter}
            operatorFilterOptions={operatorFilterOptions}
            DateRangePickerComponent={(
              <TimeRangePicker
                startDate={filterInputs.startDate}
                endDate={filterInputs.endDate}
                onChange={(start, end) => {
                  setFilterInputs((prev) => ({ ...prev, startDate: start, endDate: end }));
                }}
              />
            )}
            currentPage={page}
            pageSize={pageSize}
            totalTasks={totalTasks}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            isLoading={isQueryLoading || isListLoading}
            getTaskPreviewUrl={resolveTaskPreviewUrl}
            emptyTitle={t('studioChrome.emptyHistory')}
            emptyDescription={config.emptyDescription}
            subAccountPlaceholder={t('studioChrome.batchPh')}
            subAccountClearAriaLabel={t('studioChrome.clearBatch')}
            isBulkDeleting={isDeleting}
            enablePreviewClick
            taskExtraColumnHeader={taskExtraColumnHeader}
            renderTaskExtraCell={renderTaskExtraCell}
          />
          ) : (
            <HistoryPanelBodyLoading />
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
